/**
 * Shared tooltip copy for financial terms. Metric definitions kept
 * consistent with the WHB weekly report so the vocabulary matches
 * across surfaces.
 *
 * Each entry has a `title` (the metric name as rendered in the
 * tooltip header) and a `body` (plain-text explanation). Wire them
 * into MUI `<Tooltip>` or a custom info popover.
 */

export interface Term {
  title: string;
  body: string;
}

export const TERMS = {
  // ---- Portfolio-level ----------------------------------------------------
  portfolioVsSpy: {
    title: "Portfolio vs SPY — Risk-Adjusted Comparison",
    body:
      "Lifetime view of your portfolio's chain-linked Time-Weighted Return plotted " +
      "against SPY (price-only) and SPY+Dividends, with cashflow markers and a full " +
      "set of risk/return metrics (APY, Volatility, Max Drawdown, Alpha, Beta, " +
      "Sharpe, Calmar). The portfolio line strips out the effect of deposits and " +
      "withdrawals so it represents pure investment performance."
  },
  portfolioTwr: {
    title: "Portfolio TWR",
    body:
      "Your portfolio's chain-linked Time-Weighted Return rebased to 0% at the period " +
      "start. Deposits and withdrawals are stripped out so the curve reflects pure " +
      "investment performance."
  },
  portfolioValue: {
    title: "Portfolio Value",
    body: "Raw portfolio value over time, including the effect of deposits and withdrawals."
  },
  spyPrice: {
    title: "SPY (price)",
    body: "S&P 500 ETF (SPY) price-only return — what you'd see on a stock chart, no dividends."
  },
  spyTotal: {
    title: "SPY (total return)",
    body:
      "S&P 500 with dividends reinvested (estimated at ~1.3% annual yield prorated " +
      "to elapsed time)."
  },
  cashflows: {
    title: "Cashflows",
    body:
      "Cyan dots mark deposits, red dots mark withdrawals. Hover any dot on the chart " +
      "to see dates and amounts."
  },

  // ---- Return / Annualized -----------------------------------------------
  wpr: {
    title: "WPR — Weighted Period Return",
    body:
      "Weighted Period Return — chain-linked Time-Weighted Return over the entire " +
      "period from inception to the as-of date. PORT excludes cashflows; SPY uses " +
      "raw price; SPY+D includes estimated dividends."
  },
  weightedApy: {
    title: "Weighted APY",
    body:
      "Money-Weighted Return (IRR) for your portfolio. SPY/SPY+D rows are annualized " +
      "TWR (no cashflows). All projected to a full year for comparison across periods " +
      "of different length."
  },
  apy: {
    title: "APY — Annualized Return",
    body:
      "Annualized rate of return. Projects this account's TWR onto a one-year basis. " +
      "More meaningful for accounts that have run for at least a few months."
  },
  twr: {
    title: "TWR — Time-Weighted Return",
    body:
      "Chain-linked time-weighted return since inception. Removes the effect of " +
      "deposit/withdrawal timing so the number reflects pure investment performance " +
      "— i.e. it is NOT distorted by when you added or pulled money. Industry-" +
      "standard way to measure manager skill."
  },
  dollarReturn: {
    title: "$ Return",
    body:
      "Simple cash-on-cash return: (current value − net deposits) ÷ net deposits. " +
      "Doesn't account for how long the money has been at work, so newer accounts " +
      "can look smaller than their TWR suggests. TWR is the cleaner measure of " +
      "investment skill."
  },

  // ---- Risk / Risk-Adjusted ----------------------------------------------
  annVolatility: {
    title: "Annualized Volatility",
    body:
      "Annualized Volatility — standard deviation of weekly returns scaled by √52. " +
      "Higher = more variable; a proxy for risk in classic finance."
  },
  maxDrawdown: {
    title: "Max Drawdown",
    body:
      "Maximum Drawdown — the largest peak-to-trough decline over the period. " +
      "Worst-case loss before recovery (always negative or zero)."
  },
  alpha: {
    title: "Alpha (annualized)",
    body:
      "Jensen's Alpha (annualized) — excess return beyond what CAPM predicts given " +
      "the portfolio's beta. Positive alpha = outperformed expectations; negative = " +
      "underperformed."
  },
  beta: {
    title: "Beta",
    body:
      "Beta — sensitivity to SPY moves. 1 means moves in lockstep with SPY; >1 = " +
      "more volatile; <1 = less; 0 = uncorrelated; negative = inversely correlated."
  },
  sharpe: {
    title: "Sharpe Ratio",
    body:
      "Sharpe Ratio — return per unit of total volatility: (APY − riskFree) / " +
      "Volatility. >1 is good, >2 is great, <0 means losing money on a risk-adjusted " +
      "basis."
  },
  calmar: {
    title: "Calmar Ratio",
    body:
      "Calmar Ratio — return per unit of worst drawdown: APY / |Max Drawdown|. " +
      "Rewards strategies that earn well without large losses. Higher is better."
  },

  weeklyTwr: {
    title: "Weekly TWR",
    body:
      "Time-Weighted Return for the selected week — chain-linked daily returns " +
      "with deposits/withdrawals removed. Effective $ ≈ start value × weekly TWR."
  },
  maxRecoveryTime: {
    title: "Max recovery time (MRec)",
    body:
      "Longest stretch the account stayed below a prior peak — the worst " +
      "underwater period, including one that's still open. Measured in " +
      "calendar days, so weekends and holidays count as real waiting time. " +
      "Shorter is better: it's how long you'd have waited to get back to even. " +
      "Rule of thumb: prefer under 6 months."
  },
  rangeReturn: {
    title: "Range return",
    body:
      "Time-Weighted Return over the range selected on the chart — deposits " +
      "and withdrawals removed, compounded from the window's first day. " +
      "The Weighted APY (range) card is this same number annualised, so on a " +
      "window shorter or longer than 365 days the two differ slightly."
  },
  ytdTwr: {
    title: "YTD (TWR)",
    body:
      "Year-to-date Time-Weighted Return — TWR from January 1 of the current " +
      "year to the as-of date. Deposit/withdrawal timing is stripped out."
  },
  netDeposits: {
    title: "Net Deposits",
    body:
      "Net cash invested into this account since inception (deposits minus " +
      "withdrawals). The sum of every cashflow — authoritative across the app."
  },

  // ---- Portfolio composition ---------------------------------------------
  allocation: {
    title: "Allocation %",
    body: "Share of total portfolio value held in this bot. All bots sum to 100%."
  },
  dayChange: {
    title: "Previous Day",
    body:
      "Most recent single-trading-day change in this portfolio. The primary number is the dollar " +
      "change (green for gains, red for losses). The percent below shows the same move as a return " +
      "vs. the prior close. SPY compares the benchmark's move over the same day."
  },
  pastMonth: {
    title: "Past Month",
    body:
      "Return over the trailing 30 calendar days — matches the chart's 1M range. Shows dollar change " +
      "(cashflow-neutral) and percent return, with SPY over the same window for comparison."
  },
  positionValue: {
    title: "Value",
    body: "Current dollar value of the position held in this bot as of the report date."
  },

  // ---- Cash / Buying power ------------------------------------------------
  buyingPower: {
    title: "Total Buying Power",
    body:
      "All cash available for trading in this account, including cash currently held " +
      "by bots. Bots may use their allocated cash at any time, so this figure can " +
      "overstate what you can deploy into a new manual trade."
  },
  buyingPowerExBots: {
    title: "Buying Power (Excluding Bots)",
    body:
      "Cash available for manual trading — excludes cash already allocated to bots. " +
      "This is what you can deploy into a new position right now without touching " +
      "any bot's working capital."
  },
  stocks: {
    title: "Stocks",
    body: "Equities and ETFs currently held in this account, valued at the most recent close."
  },
  cash: {
    title: "Cash",
    body:
      "Settled cash in this account. Negative values mean you've borrowed " +
      "from your margin balance to fund positions."
  },
  holdings: {
    title: "Holdings",
    body: "Number of distinct positions currently open in this account."
  },
  botsCount: {
    title: "Bots",
    body: "Number of trading bots currently attached to this account."
  },

  // ---- Balances tab --------------------------------------------------------
  marginTotal: {
    title: "Margin Total",
    body:
      "Buying power available beyond your cash — what your broker will lend " +
      "against the value of your long positions (typically 50% of marginable " +
      "long market value under RegT)."
  },
  totalBuyingPower: {
    title: "Total",
    body:
      "Total buying power = cash + margin available to you. The headline " +
      "number above the rows. Same as Effective Buying Power."
  },
  regTBuyingPower: {
    title: "RegT Buying Power",
    body:
      "Maximum overnight buying power under Regulation T — the regulatory " +
      "ceiling for margin in a non-PDT account. Formula: 2 × (equity − " +
      "initial margin requirement)."
  },
  dayTradingBuyingPower: {
    title: "Day Trading Buying Power",
    body:
      "Buying power available specifically for round-trip trades closed the " +
      "same day. Pattern Day Trader (PDT) accounts get up to 4× maintenance " +
      "excess; non-PDT accounts typically see 0 or the same number as " +
      "Effective."
  },
  effectiveBuyingPower: {
    title: "Effective Buying Power",
    body:
      "Buying power you can actually deploy right now — the broker's " +
      "official figure, net of any margin already used and any pending " +
      "trades. The number to trust when sizing a new position."
  },
  nonMarginableBuyingPower: {
    title: "Non-Marginable Buying Power",
    body:
      "Cash you can use to buy non-marginable assets (low-priced stocks, " +
      "OTC, certain ETFs). Equal to equity − initial margin − $2,000 FINRA " +
      "minimum equity buffer."
  },
  initialMargin: {
    title: "Initial Margin",
    body:
      "Equity required at the moment you open a position — RegT sets this " +
      "at 50% of long market value. Below this, the broker won't let you " +
      "place new buys."
  },
  maintenanceMargin: {
    title: "Maintenance Margin",
    body:
      "Minimum equity you must hold AFTER a position is open — the floor " +
      "that triggers a margin call if equity drops below it. RegT minimum " +
      "is 25% of LMV; some volatile names carry 30%+."
  },
  cashWithdrawable: {
    title: "Cash Withdrawable",
    body:
      "Cash you can move out of the account today. Equal to equity − " +
      "initial margin − $2,000 FINRA minimum on a margin account; equal " +
      "to settled cash on a cash account."
  },
  pendingTransferOut: {
    title: "Pending Transfer Out",
    body:
      "Cash that's been requested for withdrawal but hasn't settled yet. " +
      "Locked from new trades until the transfer clears (usually T+2)."
  },
  pendingTransferIn: {
    title: "Pending Transfer In",
    body:
      "Cash that's been deposited but hasn't fully settled yet. Often " +
      "available for trading immediately, but can't be withdrawn until " +
      "settled."
  },
  cashAccruingInterest: {
    title: "Cash Accruing Interest",
    body:
      "Portion of your cash balance currently earning interest in the " +
      "broker's high-yield sweep program. Negative-cash margin accounts " +
      "earn nothing here — interest accrual requires positive cash."
  },
  monthlyAccruedInterest: {
    title: "Monthly Accrued Interest",
    body:
      "Interest earned on the cash sweep so far this billing month. " +
      "Posts to your account at month-end."
  },
  totalAccruedInterest: {
    title: "Total Accrued Interest",
    body:
      "All interest accumulated on cash sweep across recent months — " +
      "useful as a long-term yield check on idle cash."
  },
  equity: {
    title: "Equity",
    body:
      "Total account value at the most recent mark — cash + market value " +
      "of all positions. The number that drives margin requirements and " +
      "your gain/loss tracking."
  },
  longMarketValue: {
    title: "Long Market Value",
    body:
      "Sum of (qty × current price) for every long position. The market " +
      "value of stocks you own outright."
  },
  shortMarketValue: {
    title: "Short Market Value",
    body:
      "Sum of (qty × current price) for every short position, expressed " +
      "as a positive number. Increases in this number REDUCE your equity " +
      "(you owe more)."
  },
  positionMarketValue: {
    title: "Position Market Value",
    body:
      "Net market value of all open positions (long market value − short " +
      "market value). Excludes cash."
  },
  accruedFees: {
    title: "Accrued Fees",
    body:
      "Fees charged by the broker (regulatory, clearing, SEC/TAF, margin " +
      "interest) that haven't yet posted to your statement. Settles end of day."
  },
  heldAchDeposits: {
    title: "Held ACH Deposits",
    body:
      "ACH deposits the broker is holding past their settlement period — " +
      "usually a fraud or compliance review. Visible to you but not " +
      "deployable until released."
  },
  dayTradeCount: {
    title: "Day Trade Count",
    body:
      "Number of round-trip day trades placed in the rolling 5-business-day " +
      "window. Hitting 4 in 5 days flags the account as a Pattern Day " +
      "Trader (PDT), with stricter equity rules."
  }
} satisfies Record<string, Term>;

export type TermKey = keyof typeof TERMS;

// Risk-free rate used in Sharpe / Calmar calculations.
export const RISK_FREE_RATE_PCT = 4.5;
